import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import {
  LayoutDashboard, Building2, MonitorSpeaker, Tags, Users, History,
  ShieldCheck, Settings, LogOut, ExternalLink, ListChecks, AlertOctagon,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, roles: ["SUPER_ADMIN", "AGENCY_ADMIN", "AUDITOR"] },
  { to: "/admin/agencies", label: "Agences", icon: Building2, roles: ["SUPER_ADMIN", "AGENCY_ADMIN"] },
  { to: "/admin/counters", label: "Guichets", icon: MonitorSpeaker, roles: ["SUPER_ADMIN", "AGENCY_ADMIN"] },
  { to: "/admin/categories", label: "Catégories", icon: Tags, roles: ["SUPER_ADMIN", "AGENCY_ADMIN"] },
  { to: "/admin/users", label: "Utilisateurs", icon: Users, roles: ["SUPER_ADMIN", "AGENCY_ADMIN"] },
  { to: "/admin/history", label: "Historique", icon: History, roles: ["SUPER_ADMIN", "AGENCY_ADMIN", "AUDITOR"] },
  { to: "/admin/audit", label: "Journal d'audit", icon: ShieldCheck, roles: ["SUPER_ADMIN", "AGENCY_ADMIN", "AUDITOR"] },
  { to: "/admin/error-logs", label: "Erreurs techniques", icon: AlertOctagon, roles: ["SUPER_ADMIN", "AGENCY_ADMIN", "AUDITOR"] },
  { to: "/admin/settings", label: "Paramètres", icon: Settings, roles: ["SUPER_ADMIN", "AGENCY_ADMIN"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const visible = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center gap-3">
          <img src={LOGO_URL} alt="MAMA" className="w-11 h-11" />
          <div>
            <div className="font-display font-bold text-slate-900">MAMA</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Console Admin</div>
          </div>
        </div>
        <nav className="p-3 flex-1 space-y-1">
          {visible.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                  isActive
                    ? "bg-[#DC2626] text-white shadow-sm"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
          <div className="pt-4 mt-4 border-t border-slate-200 space-y-1">
            <a href="/kiosk" target="_blank" rel="noreferrer"
               className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
              <ListChecks className="w-4 h-4" /> Tablette accueil
              <ExternalLink className="w-3 h-3 ml-auto" />
            </a>
            <a href="/display" target="_blank" rel="noreferrer"
               className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100">
              <MonitorSpeaker className="w-4 h-4" /> Écran TV
              <ExternalLink className="w-3 h-3 ml-auto" />
            </a>
          </div>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="text-xs text-slate-500 mb-1">Connecté en tant que</div>
          <div className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</div>
          <div className="text-[11px] text-slate-500 mb-3">{user?.role}</div>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
